import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, Meter, StopRecord, Area } from '../types';
import { supabase, supabaseAdmin } from '../lib/supabaseClient';
import { userService } from '../services/userService';
import { areaService } from '../services/areaService';
import { meterService } from '../services/meterService';
import { stopRecordService } from '../services/stopRecordService';

interface AppContextType {
  currentUser: User | null;
  users: User[];
  meters: Meter[];
  areas: Area[];
  stopRecords: StopRecord[];
  loading: boolean;
  error: string | null;
  login: (email: string, pass: string) => Promise<void>;
  logout: () => Promise<void>;
  addMeter: (meter: Omit<Meter, 'id'>) => Promise<void>;
  updateMeter: (meter: Meter) => Promise<void>;
  deleteMeter: (meterId: string) => Promise<void>;
  addArea: (area: Omit<Area, 'id'>) => Promise<void>;
  updateArea: (area: Area) => Promise<void>;
  deleteArea: (areaId: string) => Promise<void>;
  addUser: (user: Omit<User, 'id'>, initialPassword?: string) => Promise<void>;
  updateUser: (user: User) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  startStop: (
    stopData: Omit<StopRecord, 'id' | 'startTime'> & { meterId: string },
    photoFile?: File | null
  ) => Promise<void>;
  endStop: (stopData: {
    recordId: string;
    endValue: number;
    endPhoto?: string;
    endPhotoFile?: File | null;
    isManualEnd: boolean;
  }) => Promise<void>;
  updateStopRecord: (record: StopRecord) => Promise<void>;
  clearError: () => void;
}

const AppContext = createContext<AppContextType>({} as AppContextType);

const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [stopRecords, setStopRecords] = useState<StopRecord[]>([]);

  const clearError = useCallback(() => setError(null), []);

  // Helper resiliente: reintenta si hay choques de "Locks" entre múltiples pestañas
  const fetchProfileSafely = async (email: string, maxRetries = 9) => {
    for (let i = 0; i < maxRetries; i++) {
      // Se utiliza `supabaseAdmin` (sin persistencia ni memoria compartida) en lugar de `supabase`
      // para esquivar absolutamente la "sala de espera" (Lock 5000ms) del token principal.
      const { data, error } = await supabaseAdmin.from('users').select('*').eq('email', email).single();
      if (error) {
        // Evitamos que un error "Lock broken by another request" destruya el login en pestañas gemelas
        if (error.message?.toLowerCase().includes('lock') || error.message?.toLowerCase().includes('steal') || error.message?.toLowerCase().includes('abort')) {
          console.warn(`[Supabase Auth] Choque de pestañas o candado ocupado (Intento ${i + 1}/${maxRetries}). Reintentando...`);
          await new Promise(res => setTimeout(res, 650)); // Esperar un momento a que la otra pestaña termine de leer
          continue;
        }
        return { data: null, error };
      }
      return { data, error: null };
    }
    return { data: null, error: { code: 'LOCK_TIMEOUT', message: 'Fallo definitivo por conflicto de ventanas múltiples.' } };
  };

  // Cargar sesión inicial de Supabase Auth de forma segura
  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && session.user.email) {
          const { data: profile, error } = await fetchProfileSafely(session.user.email);
          if (error) console.error("Error crítico obteniendo perfil inicial:", error);
          if (profile && mounted) {
            setCurrentUser(profile);
          }
        }
      } catch (err) {
        console.error("Error validando sesión inicial:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    checkSession();

    // Escuchador pasivo para cambios futuros de sesión (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Supabase Auth Event:", event);
      if (session?.user && session.user.email) {
        // Evitar consultas redundantes si el usuario ya está autenticado y es el mismo
        if (!currentUser || currentUser.email !== session.user.email) {
          const { data: profile, error } = await fetchProfileSafely(session.user.email);
          if (profile && mounted) setCurrentUser(profile);
        }
      } else if (event === 'SIGNED_OUT') {
        if (mounted) {
          setCurrentUser(null);
          setUsers([]);
          setAreas([]);
          setMeters([]);
          setStopRecords([]);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Cargar datos de la app solo cuando hay un usuario activo
  useEffect(() => {
    if (!currentUser) return;

    const loadData = async () => {
      try {
        setError(null);

        // Cargar usuarios y áreas primero en paralelo
        const [loadedUsers, loadedAreas] = await Promise.all([
          userService.fetchUsers(),
          areaService.fetchAreas()
        ]);

        setUsers(loadedUsers);
        setAreas(loadedAreas);

        // Cargar medidores y registros históricos de paradas de forma independiente.
        // Los medidores se muestran de inmediato sin esperar a los miles de registros de paradas históricas.
        meterService.fetchMeters(loadedAreas)
          .then(loadedMeters => {
            if (mounted) setMeters(loadedMeters);
          })
          .catch(err => {
            console.error("❌ Error cargando medidores:", err);
            setError('Error al cargar medidores.');
          });

        stopRecordService.fetchStopRecords()
          .then(loadedRecords => {
            if (mounted) setStopRecords(loadedRecords);
          })
          .catch(err => {
            console.error("❌ Error cargando historial de paradas:", err);
            setError('Error al cargar historial.');
          });

      } catch (err: any) {
        console.error("❌ Error cargando data base desde servicios:", err);
        setError(err.message || 'Error conectando a la base de datos de producción');
      }
    };

    let mounted = true;
    loadData();

    return () => {
      mounted = false;
    };
  }, [currentUser]);

  // Auth Supabase Real
  const login = async (email: string, pass: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password: pass });
    if (error) {
      throw new Error(error.message);
    }

    // Validar si el trigger creó el perfil, usando el lector con reintentos automáticos
    const { data: profile, error: dbError } = await fetchProfileSafely(email);

    if (dbError && dbError.code !== 'PGRST116') {
      // Si el error es de conexión o conflicto de pestañas, pero NO que falten filas
      throw new Error(`Conflicto de red o choque de ventanas detectado. Intenténtalo de nuevo. (${dbError.message})`);
    }

    if (!profile) {
      // Importante: No ponemos 'await' aquí para evitar que se congele "Validando Accesos"
      // por el conflicto (lock) de almacenamiento interno de Supabase.
      supabase.auth.signOut().catch(() => { });
      throw new Error("¡Autenticación correcta pero NO existes en la tabla de Personal (users)! Recrea tu cuenta en el panel para que el Trigger te registre.");
    }

    // Optimistic UI Update: Forzar entrada instantánea al Dashboard sin esperar al listener global
    setCurrentUser(profile);
  };

  const logout = async () => {
    // Optimistic UI Update: Forzar salida instantánea al Login Screen
    setCurrentUser(null);
    setUsers([]);
    setAreas([]);
    setMeters([]);
    setStopRecords([]);
    window.location.hash = ''; // Limpiar el router url para evitar problemas

    try {
      // Ejecutar la destrucción del backend en background
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("Error background signOut:", err);
    }
  };

  // --- METERS ---
  const addMeter = async (meter: Omit<Meter, 'id'>) => {
    try {
      setError(null);
      const newMeter = await meterService.addMeter(meter, areas);
      setMeters(prev => [...prev, newMeter]);
    } catch (err: any) {
      console.error("Error al crear medidor:", err);
      setError('Error al crear medidor: ' + (err.message || 'Error de red'));
    }
  };

  const updateMeter = async (updated: Meter) => {
    try {
      setError(null);
      await meterService.updateMeter(updated, areas);
      setMeters(prev => prev.map(m => m.id === updated.id ? updated : m));
    } catch (err: any) {
      console.error("Error al actualizar medidor:", err);
      setError('Error al actualizar medidor: ' + (err.message || 'Error de red'));
    }
  };

  const deleteMeter = async (id: string) => {
    try {
      setError(null);
      await meterService.deleteMeter(id);
      setMeters(prev => prev.filter(m => m.id !== id));
    } catch (err: any) {
      console.error("Error al eliminar medidor:", err);
      setError('Error al eliminar medidor: ' + (err.message || 'Error de red'));
    }
  };

  // --- AREAS ---
  const addArea = async (area: Omit<Area, 'id'>) => {
    try {
      setError(null);
      const newArea = await areaService.addArea(area);
      setAreas(prev => [...prev, newArea]);
    } catch (err: any) {
      console.error("Error al crear área:", err);
      setError('Error al crear área: ' + (err.message || 'Error de red'));
    }
  };

  const updateArea = async (updated: Area) => {
    try {
      setError(null);
      await areaService.updateArea(updated);
      setAreas(prev => prev.map(a => a.id === updated.id ? updated : a));
    } catch (err: any) {
      console.error("Error al actualizar área:", err);
      setError('Error al actualizar área: ' + (err.message || 'Error de red'));
    }
  };

  const deleteArea = async (id: string) => {
    try {
      setError(null);
      await areaService.deleteArea(id);
      setAreas(prev => prev.filter(a => a.id !== id));
    } catch (err: any) {
      console.error("Error al eliminar área:", err);
      setError('Error al eliminar área: ' + (err.message || 'Error de red'));
    }
  };

  // --- USERS ---
  const addUser = async (user: Omit<User, 'id'>, initialPassword?: string) => {
    try {
      setError(null);
      const newUser = await userService.addUser(user, initialPassword);
      setUsers(prev => [...prev, newUser]);
    } catch (err: any) {
      console.error("Error al crear usuario:", err);
      setError('Error al crear usuario: ' + (err.message || 'Error de red'));
    }
  };

  const updateUser = async (updated: User) => {
    try {
      setError(null);
      await userService.updateUser(updated);
      setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
    } catch (err: any) {
      console.error("Error al actualizar usuario:", err);
      setError('Error al actualizar usuario: ' + (err.message || 'Error de red'));
    }
  };

  const deleteUser = async (id: string) => {
    try {
      setError(null);
      await userService.deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (err: any) {
      console.error("Error al eliminar usuario:", err);
      setError('Error al eliminar usuario: ' + (err.message || 'Error de red'));
    }
  };

  // --- STOP RECORDS ---
  const startStop = async (
    stopData: Omit<StopRecord, 'id' | 'startTime'> & { meterId: string },
    photoFile?: File | null
  ) => {
    try {
      setError(null);
      const newRecord = await stopRecordService.startStopRecord(stopData, photoFile);
      setStopRecords(prev => [newRecord, ...prev]);
    } catch (err: any) {
      console.error("Error al registrar inicio de parada:", err);
      setError('Error al registrar arranque: ' + (err.message || 'Error de red'));
    }
  };

  const endStop = async (stopData: {
    recordId: string;
    endValue: number;
    endPhoto?: string;
    endPhotoFile?: File | null;
    isManualEnd: boolean;
  }) => {
    try {
      setError(null);
      const endResult = await stopRecordService.endStopRecord(stopData);
      setStopRecords(prev => prev.map(r =>
        r.id === stopData.recordId ? { ...r, ...endResult } : r
      ));
    } catch (err: any) {
      console.error("Error al finalizar registro de parada:", err);
      setError('Error al finalizar registro: ' + (err.message || 'Error de red'));
    }
  };

  const updateStopRecord = async (updated: StopRecord) => {
    try {
      setError(null);
      await stopRecordService.updateStopRecord(updated);
      setStopRecords(prev => prev.map(r => r.id === updated.id ? updated : r));
    } catch (err: any) {
      console.error("Error al actualizar registro de parada:", err);
      setError('Error al actualizar registro: ' + (err.message || 'Error de red'));
    }
  };

  return (
    <AppContext.Provider value={{
      currentUser, users, meters, areas, stopRecords,
      loading, error,
      login, logout,
      addMeter, updateMeter, deleteMeter,
      addArea, updateArea, deleteArea,
      addUser, updateUser, deleteUser,
      startStop, endStop, updateStopRecord,
      clearError
    }}>
      {children}
    </AppContext.Provider>
  );
};

export { AppContext, AppProvider };